/**
 * Dermowise Settings Page
 * User settings and preferences management
 */

import React, { useState, useEffect } from 'react';
import { Settings, Bell, User, Shield, Moon, Sun, LogOut, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationSetup from '../components/NotificationSetup';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('notifications');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    // Load user preferences from localStorage
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(savedDarkMode);
    
    // Load user profile
    const savedProfile = localStorage.getItem('userProfile');
    if (savedProfile) {
      setUserProfile(JSON.parse(savedProfile));
    }
  }, []);

  const handleDarkModeToggle = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    // Apply dark mode to document
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    // Clear all user data
    localStorage.removeItem('firebaseIdToken');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('darkMode');
    
    // Redirect to login
    navigate('/login');
  };

  const tabs = [
    { id: 'notifications', label: 'نوتیفیکیشن‌ها', icon: Bell },
    { id: 'profile', label: 'پروفایل', icon: User },
    { id: 'privacy', label: 'حریم خصوصی', icon: Shield },
    { id: 'appearance', label: 'ظاهر', icon: isDarkMode ? Moon : Sun }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-pink-100">
        <div className="px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <Settings className="w-6 h-6 text-pink-600" />
              <h1 className="text-xl font-bold text-gray-800">تنظیمات</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tab Navigation */}
      <div className="bg-white border-b border-pink-100">
        <div className="px-4 py-2">
          <div className="flex overflow-x-auto space-x-2 pb-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-pink-100 text-pink-700 border border-pink-200'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        <div className="max-w-md mx-auto">
          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
                <div className="flex items-center gap-3 mb-4">
                  <Bell className="w-5 h-5 text-pink-600" />
                  <h2 className="text-lg font-bold text-gray-800">تنظیمات نوتیفیکیشن</h2>
                </div>
                <NotificationSetup />
              </div>

              {/* Notification Preferences */}
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">ترجیحات نوتیفیکیشن</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-sm">یادآوری اسکن پوست</h4>
                      <p className="text-xs text-gray-600">یادآوری روزانه برای اسکن پوست</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-sm">پیشنهادات محصولات</h4>
                      <p className="text-xs text-gray-600">دریافت پیشنهادات شخصی‌سازی شده</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-sm">اخبار و مقالات</h4>
                      <p className="text-xs text-gray-600">دریافت آخرین اخبار مراقبت از پوست</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
                <div className="flex items-center gap-3 mb-4">
                  <User className="w-5 h-5 text-pink-600" />
                  <h2 className="text-lg font-bold text-gray-800">اطلاعات پروفایل</h2>
                </div>
                
                {userProfile ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                      <input
                        type="text"
                        value={userProfile.name || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                      <input
                        type="email"
                        value={userProfile.email || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">سن</label>
                      <input
                        type="number"
                        value={userProfile.age || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">نوع پوست</label>
                      <input
                        type="text"
                        value={userProfile.skinType || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                        readOnly
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">اطلاعات پروفایل یافت نشد</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-5 h-5 text-pink-600" />
                  <h2 className="text-lg font-bold text-gray-800">حریم خصوصی و امنیت</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Shield className="w-4 h-4 text-green-600" />
                      <div>
                        <h4 className="font-medium text-green-800 text-sm">اطلاعات شما امن است</h4>
                        <p className="text-xs text-green-600">تمام داده‌های شما رمزگذاری شده و محفوظ هستند</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-blue-800 text-sm">سیاست حریم خصوصی</h4>
                        <p className="text-xs text-blue-600">ما از اطلاعات شخصی شما محافظت می‌کنیم</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl p-4 shadow-lg border border-pink-100">
                <div className="flex items-center gap-3 mb-4">
                  {isDarkMode ? (
                    <Moon className="w-5 h-5 text-pink-600" />
                  ) : (
                    <Sun className="w-5 h-5 text-pink-600" />
                  )}
                  <h2 className="text-lg font-bold text-gray-800">ظاهر برنامه</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 text-sm">حالت تاریک</h4>
                      <p className="text-xs text-gray-600">استفاده از تم تاریک برای راحتی چشم</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={isDarkMode}
                        onChange={handleDarkModeToggle}
                      />
                      <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-pink-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Logout Button */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            خروج از حساب کاربری
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
